// =============================================================================
// SimSense Marketplace — Creator Workshop (Dashboard)
// =============================================================================
// OT-282: Creator management surface with KPIs, listings, publish flow,
// shop settings, and earnings overview.

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCreatorDashboard } from "../../hooks/useMarketplace";
import { PublishModal } from "./PublishModal";
import "./CreatorDashboardPage.css";

function StatusBadge({ status }: { status: string }) {
  const cls = status === "active" ? "status-active" : "status-warning";
  const label =
    status === "active"
      ? "Active"
      : status === "needs_update"
      ? "Needs Update"
      : status === "draft"
      ? "Draft"
      : "Delisted";
  return <span className={`status-badge ${cls}`}>{label}</span>;
}

export function CreatorDashboardPage() {
  const {
    publishedSetups,
    stats,
    loading,
    refresh,
    updateListing,
    removeListing,
    relistListing,
  } = useCreatorDashboard();
  const [activeMenu, setActiveMenu] = useState<
    "overview" | "setups" | "earnings" | "settings"
  >("overview");
  const [showPublish, setShowPublish] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // File input refs for logo/banner uploads
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);

  function handleImageUpload(file: File, type: "logo" | "banner") {
    const url = URL.createObjectURL(file);
    if (type === "logo") setLogoPreview(url);
    else setBannerPreview(url);
    // TODO: Upload to API when backend is wired
  }

  // Shop settings form state (mock — will be API-backed)
  const [shopForm, setShopForm] = useState({
    shopName: "",
    description: "",
    socialTwitch: "",
    socialYoutube: "",
    socialDiscord: "",
    socialTwitter: "",
  });

  async function runListingAction(listingId: string, action: () => Promise<void>) {
    setActionLoadingId(listingId);
    setActionError(null);
    try {
      await action();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Marketplace action failed.");
    } finally {
      setActionLoadingId(null);
    }
  }

  async function handleEditSetup(setup: (typeof publishedSetups)[number]) {
    const nextName = window.prompt("Edit setup name", setup.name);
    if (nextName === null) return;
    const trimmed = nextName.trim();
    if (!trimmed) {
      setActionError("Setup name cannot be empty.");
      return;
    }

    await runListingAction(setup.id, async () => {
      await updateListing(setup.id, { setupName: trimmed });
    });
  }

  async function handleUpdateSetup(setup: (typeof publishedSetups)[number]) {
    const currentPrice = setup.isFree ? "free" : ((setup.priceCents || 0) / 100).toFixed(2);
    const nextPrice = window.prompt(
      "Enter a new price in USD, or type 'free'.",
      currentPrice,
    );
    if (nextPrice === null) return;

    const normalized = nextPrice.trim().toLowerCase();
    if (!normalized) {
      setActionError("Enter a price or 'free'.");
      return;
    }

    let isFree = false;
    let priceCents = setup.priceCents || 0;
    if (normalized === "free") {
      isFree = true;
      priceCents = 0;
    } else {
      const parsed = Number.parseFloat(normalized);
      if (!Number.isFinite(parsed) || parsed < 0.99 || parsed > 50) {
        setActionError("Paid listings must be between $0.99 and $50.00.");
        return;
      }
      isFree = false;
      priceCents = Math.round(parsed * 100);
    }

    await runListingAction(setup.id, async () => {
      await updateListing(setup.id, {
        isFree,
        priceCents,
        status: setup.status === "needs_update" ? "active" : setup.status,
      });
    });
  }

  async function handleRemoveSetup(setup: (typeof publishedSetups)[number]) {
    const confirmed = window.confirm(`Delist "${setup.name}" from the marketplace?`);
    if (!confirmed) return;

    await runListingAction(setup.id, async () => {
      await removeListing(setup.id);
    });
  }

  async function handleRelistSetup(setup: (typeof publishedSetups)[number]) {
    const confirmed = window.confirm(`Relist "${setup.name}" as active?`);
    if (!confirmed) return;

    await runListingAction(setup.id, async () => {
      await relistListing(setup.id);
    });
  }

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="creator-dashboard-page">
      {/* Sidebar */}
      <aside className="dash-sidebar">
        <div className="dash-sidebar-header">
          <h2>Creator Workshop</h2>
          <Link to="/marketplace" className="dash-back-link">
            Back to Marketplace
          </Link>
        </div>

        <nav className="dash-sidebar-nav">
          {(
            [
              { key: "overview", label: "Overview" },
              { key: "setups", label: "My Setups" },
              { key: "earnings", label: "Earnings" },
              { key: "settings", label: "Shop Settings" },
            ] as const
          ).map((item) => (
            <button
              key={item.key}
              className={`dash-nav-btn ${
                activeMenu === item.key ? "active" : ""
              }`}
              onClick={() => setActiveMenu(item.key)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="dash-main">
        {actionError && (
          <div className="dash-content-card" style={{ marginBottom: "20px", color: "var(--danger, #d9534f)" }}>
            {actionError}
          </div>
        )}

        {/* ─── Overview Tab ─── */}
        {activeMenu === "overview" && (
          <>
            <div className="dash-header-flex">
              <h1>Overview</h1>
              <button className="dash-btn-primary" onClick={() => setShowPublish(true)}>
                + Publish New Setup
              </button>
            </div>

            {/* KPI Cards */}
            <div className="kpi-grid">
              <div className="kpi-card">
                <span className="kpi-label">Total Downloads</span>
                <span className="kpi-value">
                  {stats.totalDownloads.toLocaleString()}
                </span>
              </div>
              <div className="kpi-card">
                <span className="kpi-label">Total Earnings (MTD)</span>
                <span className="kpi-value kpi-earnings">
                  ${(stats.totalEarnings / 100).toFixed(2)}
                </span>
              </div>
              <div className="kpi-card">
                <span className="kpi-label">Average Rating</span>
                <span className="kpi-value">
                  {stats.avgRating.toFixed(1)} / 5.0
                </span>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="dash-content-card">
              <h2>Published Setups</h2>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Car</th>
                    <th>Status</th>
                    <th>Downloads</th>
                    <th>Revenue</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {publishedSetups.map((setup) => (
                    <tr key={setup.id}>
                      <td className="fw-600">{setup.name}</td>
                      <td>{setup.car}</td>
                      <td>
                        <StatusBadge status={setup.status} />
                      </td>
                      <td>{setup.downloads.toLocaleString()}</td>
                      <td>
                        {setup.revenue > 0
                          ? `$${(setup.revenue / 100).toFixed(2)}`
                          : "-"}
                      </td>
                      <td>
                        <button
                          className="dash-btn-icon"
                          disabled={actionLoadingId === setup.id}
                          onClick={() => { void handleEditSetup(setup); }}
                        >
                          {actionLoadingId === setup.id ? "Saving..." : "Edit"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ─── My Setups Tab ─── */}
        {activeMenu === "setups" && (
          <>
            <div className="dash-header-flex">
              <h1>My Setups</h1>
              <button className="dash-btn-primary" onClick={() => setShowPublish(true)}>
                + Publish New Setup
              </button>
            </div>

            {/* Filter bar */}
            <div className="dash-filter-bar">
              <button className="dash-filter-pill active">All ({publishedSetups.length})</button>
              <button className="dash-filter-pill">
                Active ({publishedSetups.filter(s => s.status === "active").length})
              </button>
              <button className="dash-filter-pill">
                Needs Update ({publishedSetups.filter(s => s.status === "needs_update").length})
              </button>
              <button className="dash-filter-pill">Drafts (0)</button>
            </div>

            {/* Setup cards with more detail */}
            <div className="dash-setup-list">
              {publishedSetups.map((setup) => (
                <div key={setup.id} className="dash-setup-card">
                  <div className="dash-setup-cover">
                    <div className="dash-setup-cover-placeholder">No Cover Photo</div>
                  </div>
                  <div className="dash-setup-info">
                    <div className="dash-setup-row">
                      <h3>{setup.name}</h3>
                      <StatusBadge status={setup.status} />
                    </div>
                    <p className="dash-setup-car">{setup.car}</p>
                    <div className="dash-setup-stats">
                      <span>{setup.downloads.toLocaleString()} downloads</span>
                      <span>{setup.price}</span>
                      {setup.revenue > 0 && (
                        <span className="dash-setup-revenue">
                          ${(setup.revenue / 100).toFixed(2)} earned
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="dash-setup-actions">
                    <button
                      className="dash-btn-icon"
                      disabled={actionLoadingId === setup.id}
                      onClick={() => { void handleEditSetup(setup); }}
                    >
                      {actionLoadingId === setup.id ? "Saving..." : "Edit"}
                    </button>
                    <button
                      className="dash-btn-icon"
                      disabled={actionLoadingId === setup.id}
                      onClick={() => { void handleUpdateSetup(setup); }}
                    >
                      {actionLoadingId === setup.id ? "Saving..." : "Update"}
                    </button>
                    {setup.status === "active" ? (
                      <button
                        className="dash-btn-icon dash-btn-danger"
                        disabled={actionLoadingId === setup.id}
                        onClick={() => { void handleRemoveSetup(setup); }}
                      >
                        {actionLoadingId === setup.id ? "Working..." : "Remove"}
                      </button>
                    ) : (
                      <button
                        className="dash-btn-icon"
                        disabled={actionLoadingId === setup.id}
                        onClick={() => { void handleRelistSetup(setup); }}
                      >
                        {actionLoadingId === setup.id ? "Working..." : "Relist"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ─── Earnings Tab ─── */}
        {activeMenu === "earnings" && (
          <>
            <h1>Earnings</h1>

            <div className="kpi-grid" style={{ marginBottom: "28px" }}>
              <div className="kpi-card">
                <span className="kpi-label">This Month</span>
                <span className="kpi-value kpi-earnings">
                  ${(stats.totalEarnings / 100).toFixed(2)}
                </span>
              </div>
              <div className="kpi-card">
                <span className="kpi-label">All Time</span>
                <span className="kpi-value kpi-earnings">
                  ${(stats.totalEarnings / 100).toFixed(2)}
                </span>
              </div>
              <div className="kpi-card">
                <span className="kpi-label">Pending Payout</span>
                <span className="kpi-value">$0.00</span>
              </div>
            </div>

            <div className="dash-content-card">
              <h2>Revenue by Setup</h2>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Setup</th>
                    <th>Price</th>
                    <th>Sales</th>
                    <th>Revenue (Your Share)</th>
                  </tr>
                </thead>
                <tbody>
                  {publishedSetups.filter(s => s.revenue > 0).map((setup) => {
                    const sales = setup.price !== "Free" ? Math.round(setup.revenue / (parseInt(setup.price.replace("$", "")) * 100 * 0.8)) || 0 : 0;
                    return (
                      <tr key={setup.id}>
                        <td className="fw-600">{setup.name}</td>
                        <td>{setup.price}</td>
                        <td>{sales}</td>
                        <td className="dash-setup-revenue">${(setup.revenue / 100).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  {publishedSetups.filter(s => s.revenue > 0).length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center", color: "var(--text2)" }}>
                        No revenue yet. Publish a paid setup to start earning.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="dash-content-card" style={{ marginTop: "20px" }}>
              <h2>Payout Settings</h2>
              <p className="dash-muted">
                Payouts are processed monthly via Stripe. Connect your Stripe account to receive earnings.
              </p>
              <button className="dash-btn-primary" style={{ marginTop: "12px" }}>
                Connect Stripe Account
              </button>
            </div>
          </>
        )}

        {/* ─── Shop Settings Tab ─── */}
        {activeMenu === "settings" && (
          <>
            <h1>Shop Settings</h1>

            <div className="dash-content-card">
              <h2>Branding</h2>

              {/* Logo upload */}
              <div className="dash-form-group">
                <label className="dash-form-label">Shop Logo</label>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file, "logo");
                  }}
                />
                <div className="dash-upload-area" onClick={() => logoInputRef.current?.click()}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="Shop logo" className="dash-upload-preview dash-upload-preview-logo" />
                  ) : (
                    <div className="dash-upload-placeholder">
                      <span className="dash-upload-icon">+</span>
                      <span>Upload Logo (128x128 recommended)</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Banner upload */}
              <div className="dash-form-group">
                <label className="dash-form-label">Shop Banner</label>
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file, "banner");
                  }}
                />
                <div className="dash-upload-area dash-upload-banner" onClick={() => bannerInputRef.current?.click()}>
                  {bannerPreview ? (
                    <img src={bannerPreview} alt="Shop banner" className="dash-upload-preview dash-upload-preview-banner" />
                  ) : (
                    <div className="dash-upload-placeholder">
                      <span className="dash-upload-icon">+</span>
                      <span>Upload Banner (1200x300 recommended)</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="dash-form-group">
                <label className="dash-form-label">Shop Name</label>
                <input
                  className="dash-form-input"
                  type="text"
                  value={shopForm.shopName}
                  onChange={(e) => setShopForm((f) => ({ ...f, shopName: e.target.value }))}
                  maxLength={60}
                />
              </div>

              <div className="dash-form-group">
                <label className="dash-form-label">Description</label>
                <textarea
                  className="dash-form-textarea"
                  value={shopForm.description}
                  onChange={(e) => setShopForm((f) => ({ ...f, description: e.target.value }))}
                  rows={4}
                  maxLength={500}
                />
              </div>
            </div>

            <div className="dash-content-card" style={{ marginTop: "20px" }}>
              <h2>Social Links</h2>
              <p className="dash-muted">Show these on your public shop profile.</p>

              {([
                { key: "socialTwitch", label: "Twitch", placeholder: "https://twitch.tv/..." },
                { key: "socialYoutube", label: "YouTube", placeholder: "https://youtube.com/..." },
                { key: "socialDiscord", label: "Discord", placeholder: "https://discord.gg/..." },
                { key: "socialTwitter", label: "X / Twitter", placeholder: "https://x.com/..." },
              ] as const).map(({ key, label, placeholder }) => (
                <div className="dash-form-group" key={key}>
                  <label className="dash-form-label">{label}</label>
                  <input
                    className="dash-form-input"
                    type="url"
                    value={shopForm[key]}
                    onChange={(e) => setShopForm((f) => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                  />
                </div>
              ))}
            </div>

            <div className="dash-content-card" style={{ marginTop: "20px" }}>
              <h2>Auto-Install Settings</h2>
              <p className="dash-muted">
                When enabled, purchased setups are pushed directly to the buyer's iRacing setup folder
                under your shop's branded subfolder.
              </p>
              <div className="dash-form-group">
                <label className="dash-form-label">Folder Name</label>
                <input
                  className="dash-form-input"
                  type="text"
                  value={shopForm.shopName}
                  disabled
                  title="Uses your shop name"
                />
                <span className="dash-form-hint">
                  Example: iRacing/setups/car_name/{shopForm.shopName}/2026/S2/Track Name/
                </span>
              </div>
            </div>

            <button className="dash-btn-primary" style={{ marginTop: "20px" }}>
              Save Settings
            </button>
          </>
        )}
      </main>

      {showPublish && (
        <PublishModal
          onClose={() => setShowPublish(false)}
          onPublished={() => {
            void refresh();
          }}
        />
      )}
    </div>
  );
}
